import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { apiEndpoint } from '../../constants/api';
import { updateUser } from '../../store/user';
import { PhotoIcon, UserCircleIcon } from '@heroicons/react/24/solid';
import '../Global.css';
import logo from '../../svg/logo.svg';

export default function JobSeekerRegistration() {
  const [buttonDisabledAttr, setDisabled] = useState(true);
  const formFields = [
    'firstName',
    'lastName',
    'phone',
    'email',
    'password',
    'confirmPassword',
    'title',
  ];

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const redirectToOnboarding = (userId) =>
    navigate('/job-seeker/onboarding/');

  useEffect(() => {
    document.title = 'Job Seeker Registration :: Productions.com';
  }, []);

  async function createAccount(event) {
    event.preventDefault();
    const formData = new FormData(event.target);
    const data = Object.fromEntries(formData.entries());
    const errors = validateForm(data);
    const errorBlock = document.getElementById('formErrors');
    if (errors.length === 0) {
      setDisabled(true);
      updateSubmitButton(false);
      disableForm();
      errorBlock.innerHTML = '<span>Creating your account...</span>';

      try {
        const { data: formData } = await axios.post(
          apiEndpoint(`/registration`),
          data
        );
        errorBlock.classList.remove('with-errors');
        errorBlock.innerHTML = '';
        enableForm();
        clearForm();
        dispatch(updateUser(formData));
        console.log('formData', formData);
        redirectToOnboarding(formData.id);
      } catch (error) {
        enableForm();
        errorBlock.innerHTML =
          '<ul><li>Error creating your account. Please try again!</li></ul>';
        errorBlock.classList.add('with-errors');
      }
    } else {
      enableForm();
      errorBlock.innerHTML =
        '<ul><li>' + errors.join('<li/><li>') + '</li></ul>';
      errorBlock.classList.add('with-errors');
    }
  }

  function disableForm() {
    const formEl = document.getElementById('createAccountForm');
    for (var i = 0; i < formEl.elements.length; i++) {
      formEl.elements[i].setAttribute('disabled', true);
    }
  }

  function enableForm() {
    const formEl = document.getElementById('createAccountForm');
    for (var i = 0; i < formEl.elements.length; i++) {
      formEl.elements[i].removeAttribute('disabled');
    }
  }

  function clearForm() {
    document.getElementById('createAccountForm').reset();
  }

  function validateForm(data) {
    const errors = [];

    if (data['first_name'].length === 0) errors.push('Enter your first name');

    if (data['last_name'].length === 0) errors.push('Enter your last name');

    if (data['title'].length === 0) errors.push('Enter your title');

    const validEmailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (!data['email'].match(validEmailRegex))
      errors.push('Enter a valid email');

    const validPhoneRegex =
      /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    if (!data['phone_number'].match(validPhoneRegex))
      errors.push('Enter a valid phone number');

    //const passwordErrors = validatePassword(data['password'], data['confirm_password']);
    //return errors.concat(passwordErrors);

    return errors;
  }

  // Password Requirements
  // minimum 6 characters, including a lower case, an upper case, and a digit)
  function validatePassword(password, confirmPassword) {
    const pswdErrors = [];

    if (password.length < 6)
      pswdErrors.push('Enter a password with at least 6 characters');
    if (!/[A-Z]/.test(password))
      pswdErrors.push('Enter a password with at least 1 upper case character');
    if (!/[a-z]/.test(password))
      pswdErrors.push('Enter a password with at least 1 lower case character');
    if (!/[0-9]/.test(password))
      pswdErrors.push('Enter a password with at least 1 digit');
    if (pswdErrors.length === 0 && password !== confirmPassword)
      pswdErrors.push('Confirm your password');

    return pswdErrors;
  }

  function liveValidation(event) {
    if (formFields.indexOf(event.target.id) > -1) {
      const formEl = document.getElementById('createAccountForm');
      var basicValidation = true;
      for (var i = 0; i < formEl.elements.length; i++) {
        if (formFields.indexOf(formEl.elements[i].id) > -1)
          basicValidation =
            basicValidation && formEl.elements[i].value.length > 0;
      }
      setDisabled(!basicValidation);
      updateSubmitButton(basicValidation);
    }
  }

  function updateSubmitButton(basicValidation) {
    const submitBtn = document.getElementById('createAccountButton');
    if (basicValidation) {
      submitBtn.classList.add('valid');
      submitBtn.classList.remove('invalid');
    } else {
      submitBtn.classList.add('invalid');
      submitBtn.classList.remove('valid');
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8 p-8">
      <form
        id="createAccountForm"
        method="post"
        onSubmit={createAccount}
        onKeyUp={liveValidation}
      >
        <div className="space-y-12">
          <div className="border-b border-gray-900/10 pb-12">
            <div className="p-6 bg-blue-600 rounded-lg"><img className="mx-auto" src={logo} alt="Productions" /></div>
            <hr className="p-0.5 m-auto bg-gray-200 border-1 rounded" />
            <h2 className="formTitle text-base font-semibold leading-7 text-gray-900">
              Sign up to find production work
            </h2>

            <div className="border-b border-gray-900/10 pb-12">
              <div id="formErrors"></div>

              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label
                    htmlFor="firstName"
                    className="required block text-sm font-medium leading-6 text-gray-900"
                  >
                    First name
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="first_name"
                      id="firstName"
                      autoComplete="first_name"
                      placeholder="First Name"
                    className="block w-full p-4 px-10 text-xl text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label
                    htmlFor="lastName"
                    className="required block text-sm font-medium leading-6 text-gray-900"
                  >
                    Last name
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="last_name"
                      id="lastName"
                      autoComplete="family-name"
                      placeholder="Last Name"
                    className="block w-full p-4 px-10 text-xl text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="col-span-full">
                  <label
                    htmlFor="title"
                    className="required block text-sm font-medium leading-6 text-gray-900"
                  >
                    Title
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="title"
                      id="title"
                      autoComplete="title"
                      placeholder="Title"
                    className="block w-full p-4 px-10 text-xl text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="col-span-full">
                  <label
                    htmlFor="email"
                    className="required block text-sm font-medium leading-6 text-gray-900"
                  >
                    Email
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Email"
                      autoComplete="email"
                    className="block w-full p-4 px-10 text-xl text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="col-span-full">
                  <label
                    htmlFor="phone"
                    className="required block text-sm font-medium leading-6 text-gray-900"
                  >
                    Phone Number
                  </label>
                  <div className="mt-2">
                    <input
                      type="tel"
                      name="phone_number"
                      id="phone"
                      placeholder="Phone Number"
                      autoComplete="phone"
                    className="block w-full p-4 px-10 text-xl text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center gap-x-6">
              <p className="mt-1 text-sm leading-6 text-gray-600">
                By continuing, I understand and agree to the{' '}
                <a className="formLink" href="/terms-of-use">
                  Productions Terms of Service
                </a>
                , including the{' '}
                <a className="formLink" href="/user-agreement">
                  User Agreement
                </a>{' '}
                and{' '}
                <a className="formLink" href="/privacy-policy">
                  Privacy Policy
                </a>
                .
              </p>
            </div>

            <div className="mt-6 flex items-center justify-center gap-x-6">
              <button
                type="submit"
                id="createAccountButton"
                disable={buttonDisabledAttr.toString()}
                className="text-white bg-blue-600 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-xl inline-flex items-center px-5 py-2.5 text-center mr-2"
              >
                Create account
              </button>
            </div>

            <div className="mt-6 flex items-center justify-center gap-x-6">
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Already have an account?{' '}
                <a className="formLink" href="/">
                  Log in
                </a>
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
